import React, { useCallback, useEffect, useRef, useState } from "react"
import { useHistory } from "react-router-dom"
import dayjs from "dayjs"
import { DateRangePicker } from "rsuite"
import "rsuite/dist/rsuite.min.css"
import Select from "react-select"
import { Button, Card, CardBody, Col, Label, Row, Spinner, Table } from "reactstrap"
import { getPredefinedDateRanges } from "helpers/dateRanges"
import { getAccountingPartnerReport } from "helpers/backendHelpers/accountingPartnerReport"

export const formatRupees = value =>
  `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`

const SummaryCard = ({ label, value, hint }) => (
  <Col sm={6} xl={3} className="mb-3">
    <Card className="mb-0 h-100">
      <CardBody>
        <p className="text-muted mb-1 font-size-13">{label}</p>
        <h4 className="mb-0">{value}</h4>
        {hint && <p className="text-muted mb-0 mt-1 font-size-12">{hint}</p>}
      </CardBody>
    </Card>
  </Col>
)

const EMPTY_FILTERS = { startDate: "", endDate: "", partnerId: "" }

const partnerOption = partner => ({
  value: partner.partnerId,
  label: partner.name,
  detail: [partner.mobileNo, partner.firmName].filter(Boolean).join(" · "),
})

// Typing matches the name, firm or mobile, and a mobile may be typed as shown ("+91 93534 53453").
const matchesPartner = ({ data }, input) => {
  const text = input.trim().toLowerCase()
  if (!text) return true
  const digits = text.replace(/D/g, "").replace(/^91(?=d{10}$)/, "")
  return `${data.label} ${data.detail}`.toLowerCase().includes(text) || Boolean(digits && data.detail.replace(/D/g, "").includes(digits))
}
const DATE_FORMAT = "YYYY-MM-DD"

const AccountingPartnerReport = () => {
  const history = useHistory()
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [report, setReport] = useState(null)
  // Every partner, for the dropdown; the report itself shrinks to the chosen partner.
  const [partnerOptions, setPartnerOptions] = useState([])
  const [loading, setLoading] = useState(true)

  // Only the newest request may update the page.
  const latest = useRef(0)
  const load = useCallback(async applied => {
    const current = ++latest.current
    setLoading(true)
    try {
      const params = Object.fromEntries(Object.entries(applied).filter(([, value]) => value))
      const result = await getAccountingPartnerReport(params)
      if (current === latest.current) {
        setReport(result.payload)
        if (!applied.partnerId) setPartnerOptions(result.payload.partners.map(partnerOption))
      }
    } catch (_) {
      // api_helper already shows the error toast
    } finally {
      if (current === latest.current) setLoading(false)
    }
  }, [])

  // Filters apply as they change; typing in search waits a moment.
  useEffect(() => {
    load(filters)
  }, [filters, load])

  const hasFilters = Object.values(filters).some(Boolean)
  const summary = report?.summary
  const partners = report?.partners || []

  return (
    <div>
      <Card>
        <CardBody>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Label className="mb-1">Partner</Label>
              <Select
                options={partnerOptions}
                value={partnerOptions.find(option => option.value === filters.partnerId) || null}
                onChange={option => setFilters(prev => ({ ...prev, partnerId: option ? option.value : "" }))}
                filterOption={matchesPartner}
                formatOptionLabel={option => (
                  <div>
                    <div>{option.label}</div>
                    {option.detail && <div className="text-muted font-size-12">{option.detail}</div>}
                  </div>
                )}
                placeholder="All partners"
                isClearable
              />
            </Col>
            <Col md={5}>
              <Label className="mb-1">Lead date range</Label>
              <DateRangePicker
                value={filters.startDate && filters.endDate ? [dayjs(filters.startDate).toDate(), dayjs(filters.endDate).toDate()] : null}
                onChange={value => setFilters(prev => ({
                  ...prev,
                  startDate: value ? dayjs(value[0]).format(DATE_FORMAT) : "",
                  endDate: value ? dayjs(value[1]).format(DATE_FORMAT) : "",
                }))}
                format="dd/MM/yyyy"
                placeholder="All dates"
                showOneCalendar
                block
                ranges={getPredefinedDateRanges({ allowFuture: false })}
                shouldDisableDate={DateRangePicker.afterToday()}
              />
            </Col>
            <Col md={2}>
              <Button color="light" className="w-100" type="button" disabled={!hasFilters} onClick={() => setFilters(EMPTY_FILTERS)}>
                Clear
              </Button>
            </Col>
          </Row>
          <p className="text-muted font-size-12 mb-0 mt-2">
            Dates filter the leads by the day each partner added them. Conversion is a lead that bought a paid plan.
          </p>
        </CardBody>
      </Card>

      {summary && (
        <Row>
          <SummaryCard label="Total selling" value={formatRupees(summary.totalSelling)} hint={`Collected with GST ${formatRupees(summary.totalCollected)}`} />
          <SummaryCard label="Conversion rate" value={`${summary.conversionRate}%`} hint={`${summary.converted} of ${summary.totalLeads} leads converted`} />
          <SummaryCard label="Total leads" value={summary.totalLeads} hint={`${summary.demoCompleted} reached demo`} />
          <SummaryCard label="Partners" value={summary.partners} hint={`${summary.activePartners} sent leads`} />
        </Row>
      )}

      <Card>
        <CardBody>
          {loading && !report ? (
            <div className="text-center py-4"><Spinner color="primary" /></div>
          ) : (
            <div className="table-responsive" style={{ opacity: loading ? 0.6 : 1 }}>
              <Table className="align-middle table-hover mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Partner</th>
                    <th className="text-end">Leads</th>
                    <th className="text-end">Demo completed</th>
                    <th className="text-end">Converted</th>
                    <th className="text-end">Conversion</th>
                    <th className="text-end">Total selling</th>
                  </tr>
                </thead>
                <tbody>
                  {partners.map((partner, index) => (
                    <tr
                      key={partner.partnerId}
                      role="link"
                      style={{ cursor: "pointer" }}
                      onClick={() => history.push(`/setting/accounting-partners/${partner.partnerId}`)}
                    >
                      <td>{index + 1}</td>
                      <td>
                        <div className="fw-semibold">{partner.name}</div>
                        <div className="text-muted font-size-12">
                          {[partner.mobileNo, partner.firmName].filter(Boolean).join(" · ")}
                        </div>
                      </td>
                      <td className="text-end">{partner.totalLeads}</td>
                      <td className="text-end">{partner.demoCompleted}</td>
                      <td className="text-end">{partner.converted}</td>
                      <td className="text-end">{partner.conversionRate}%</td>
                      <td className="text-end fw-semibold">{formatRupees(partner.totalSelling)}</td>
                    </tr>
                  ))}
                  {!partners.length && (
                    <tr><td colSpan={7} className="text-center text-muted py-4">No accounting partners found.</td></tr>
                  )}
                </tbody>
                {summary && partners.length > 0 && (
                  <tfoot>
                    <tr className="fw-bold table-light">
                      <td colSpan={2}>Total</td>
                      <td className="text-end">{summary.totalLeads}</td>
                      <td className="text-end">{summary.demoCompleted}</td>
                      <td className="text-end">{summary.converted}</td>
                      <td className="text-end">{summary.conversionRate}%</td>
                      <td className="text-end">{formatRupees(summary.totalSelling)}</td>
                    </tr>
                  </tfoot>
                )}
              </Table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

export default AccountingPartnerReport
